/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useDebouncedCallback } from 'use-debounce';
import barcodeValidationApi from '../../api/barcodeValidation.api';
import { ValidationState } from '../../shared/enums/ValidationState';

export const ValidateActivationEvent = Object.freeze({
  CHANGE: Symbol('onChange'),
  BLUR: Symbol('onBlur'),
  FOCUS: Symbol('onFocus'),
  ALL: Symbol('all'),
});

export const ValidationTypes = Object.freeze({
  BUSINESS_RULES_VALIDATION: 'INTERNAL',
  EXTERNAL_VALIDATION: 'EXTERNAL',
});

const useValidatePatternField = (props) => {
  const { onChange, feeBarcode, formatValidationResult, externalValidation, validationOrder } = props;

  const DEBOUNCE_TIME = 500;
  const [validation, setValidation] = useState(ValidationState.IDLE);
  const bindWithFormDataFn = useDebouncedCallback(onChange, DEBOUNCE_TIME);
  const [value, setValue] = useState(feeBarcode);
  const FIRST_STEP_OF_VALIDATION = validationOrder[0];
  const SECOND_STEP_OF_VALIDATION = validationOrder[1];
  const processExternalApiValidationFn = useDebouncedCallback(processExternalApiValidation, DEBOUNCE_TIME);

  useEffect(() => {
    setValue(feeBarcode);
  }, [feeBarcode]);

  useEffect(() => {
    if (
      value == null ||
      formatValidationResult === null ||
      _.isEmpty(formatValidationResult) ||
      formatValidationResult[value] === null
    ) {
      setValidation(ValidationState.IDLE);
      return;
    }
    if (validation === ValidationState.VALIDATING || validation === ValidationState.IDLE) {
      checkBarcodeValidationResult();
    }
  }, [formatValidationResult]);

  async function processExternalApiValidation() {
    const endPoint = externalValidation?.endpoint.replaceAll(externalValidation?.inputKey, value);
    const result = await barcodeValidationApi.validate(endPoint);
    if (SECOND_STEP_OF_VALIDATION === ValidationTypes.BUSINESS_RULES_VALIDATION) {
      if (result === null) {
        setValidation(ValidationState.INVALID);
        return;
      }
      onChange(value);
    }

    const externalValidationStatus = !result ? ValidationState.INVALID : ValidationState.VALID;
    setValidation(externalValidationStatus);
    onChange(value, !!result);
  }

  const checkBarcodeValidationResult = async () => {
    if (SECOND_STEP_OF_VALIDATION === ValidationTypes.EXTERNAL_VALIDATION && formatValidationResult[value]) {
      await processExternalApiValidation();
      return;
    }
    setValidation(!formatValidationResult[value] ? ValidationState.INVALID : ValidationState.VALID);
  };

  const startValidationProcess = async () => {
    setValidation(ValidationState.VALIDATING);
    if (FIRST_STEP_OF_VALIDATION === ValidationTypes.EXTERNAL_VALIDATION && externalValidation?.endpoint) {
      await processExternalApiValidation();
      return;
    }
    onChange(value);

    if (props.formatValidationResult && props.formatValidationResult[value] !== undefined) {
      checkBarcodeValidationResult();
    }
  };

  const startValidationProcessForOnChange = async (val) => {
    setValidation(ValidationState.VALIDATING);
    if (FIRST_STEP_OF_VALIDATION === ValidationTypes.EXTERNAL_VALIDATION && externalValidation?.endpoint) {
      processExternalApiValidationFn();
      return;
    }
    bindWithFormDataFn(val);
  };

  return {
    validation,
    value,
    setValue,
    startValidationProcess,
    startValidationProcessForOnChange,
  };
};

export default useValidatePatternField;
